import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { baseOnboardingUrl } from './hooks';
import { secureFetch } from '../../../common';

// const baseUrl = 'http://localhost:5002';
const baseUrl = baseOnboardingUrl;

export { secureFetch };

export const fetchEmptyOnboarding = createAsyncThunk(
  'onboarding/fetchEmptyOnboarding',
  async () => {
    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/empty_onboarding`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    const data = await response.json();
    console.log("Empty onboarding: ", data.onboarding);
    return data.onboarding || data;
  }
)

export const markCheckListItem = createAsyncThunk(
  'onboarding/markCheckListItem',
  async ({ onboardingId, stage, checkListItem }) => {
    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/checklist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        onboardingId,
        stage,
        checkListItem
      })
    });
    if (!response.ok) {
      throw new Error('Failed to mark checklist item');
    }
    const data = await response.json();
    console.log("Marked checklist item: ", data.onboarding);
    return data.onboarding;
  }
)

export const updateOnboardingFeature = createAsyncThunk(
  'onboarding/updateOnboardingFeature',
  async ({ onboardingId, feature, featureStatus }) => {
    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/feature`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        onboardingId,
        feature,
        featureStatus
      })
    });
    if (!response.ok) {
      throw new Error('Failed to update onboarding feature');
    }
    const data = await response.json();
    console.log("Updated onboarding feature: ", data.onboarding);
    return data.onboarding;
  }
)

export const updateProductType = createAsyncThunk(
  'onboarding/updateProductType',
  async ({ id, productType }) => {
    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/product_type`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id,
        productType
      })
    });
    if (!response.ok) {
      throw new Error('Failed to update product type');
    }
    const data = await response.json();
    console.log("Updated product type: ", data.onboarding);
    return data.onboarding;
  }
)

export const linkToPractice = createAsyncThunk(
  'onboarding/linkToPractice',
  async ({ id, practiceId, isDso }) => {
    const history = {
      timestamp: new Date().toISOString(),
      event: {
        from: false, // Will be updated based on current state
        to: true
      },
      trigger: {
        source: "user",
        id: "user-id"
      },
      data: {
        practiceId: practiceId,
        isDso: isDso
      },
      note: "Linked to practice"
    };
    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/link_to_practice`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
         id,
         practice_id: practiceId,
         isDso,
         history
       })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to link to practice');
    }
    const data = await response.json();
    console.log("Linked to practice: ", data.onboarding);
    return data.onboarding;
  }
)


export const createOnboarding = createAsyncThunk(
  'onboarding/create',
  async ({ onboarding }) => {
    const response = await fetch(`${baseUrl}/api/onboardings_view/general/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(onboarding)
    });
    if (!response.ok) {
      throw new Error('Failed to create onboarding');
    }
    const data = await response.json();
    console.log("New onboarding: ", data.onboarding);
    return data.onboarding || data;
  }
)

// Async thunk for fetching onboardings
export const fetchOnboardings = createAsyncThunk(
  'onboarding/fetchOnboardings',
  async () => {
    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/onboardings`);
    if (!response.ok) {
      throw new Error('Failed to fetch onboardings');
    }
    else {
      const data = await response.json();
      console.log("Onboardings: ", data.onboardings);
      return data.onboardings || [];
    }
  }
);

export const heartbeat = createAsyncThunk(
  'onboarding/heartbeat',
  async () => {
    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/heartbeat`);
    if (!response.ok) {
      throw new Error('Failed to fetch all onboardings heartbeat');
    }
    const data = await response.json();
    console.log("Heartbeat: ", data.heartbeat);
    return data.heartbeat || [];
  }
);

// Async thunk for heartbeat updates
export const fetchOnboarding = createAsyncThunk(
  'onboarding/fetch-by-id',
  async ({ onboardingId }) => {
    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/${onboardingId}/all_info`);
    if (!response.ok) {
      throw new Error('Failed to fetch onboarding heartbeat');
    }
    const data = await response.json();
    console.log("Onboarding: ", data.onboarding);
    return data.onboarding || data;
  }
);

export const fetchPractice = createAsyncThunk(
  'onboarding/fetch-practice',
  async ({ practiceId }) => {
    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/practice/${practiceId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch practice');
    }
    const data = await response.json();
    console.log("Practice: ", data.practice);
    return data.practice || data;
  }
)



const initialOnboardingState = {
  onboardings: [],
  practices: {},  // {onboarding_id: practice_doc} 
  modalOnboarding: null,
  modalPractice: null,
  showModal: false,
  showCreateModal: false,
  modalStage: 'overview',
  heartbeats: [],
  loading: false,
  error: null,
  lastHeartbeat: null,
  changedOnboardingIds: [], // Track which onboardings have changed
  timestamps: {}, // Store individual timestamps for comparison
  rowId: {}, // Store row index for each onboarding
  history_lengths: {}, // Store history lengths for each cell.   The index is [row * row_length + column]
  emptyOnboarding: {
    name: '',
    signedDate: '',
    kickoff: ''
  },
  filterDsoOnboardings: false,
  sortDsoOnboardings: true,
  // newOnboarding: {},          <-- use this to save state of modal in future
  // modalOnboardingForms: {}   <-- use this to save state of modal in future
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState: initialOnboardingState,
  reducers: {
    clearChangedOnboardingIds: (state) => {
      state.changedOnboardingIds = [];
    },
    updateTimestamp: (state, action) => {
      const { id, timestamp } = action.payload;
      state.timestamps[id] = timestamp;
    },
    clearChangedCells: (state) => {
      state.history_lengths = {};
    },
    setModalOnboarding: (state, action) => {
      const { onboardingId, stage } = action.payload;
      if (state.modalOnboarding && state.modalOnboarding.id === onboardingId) {
        state.modalStage = stage;
        state.showModal = true;
        console.log("Same onboarding selected, just need to display it")
        return;
      }
      console.log("Setting modal onboarding: ", onboardingId, stage);
      const tmp = state.onboardings.find(onboarding => onboarding.id === onboardingId);
      console.log("Modal onboarding: ", tmp);
      if (tmp) {
        state.modalOnboarding = tmp;
        console.log("Modal onboarding: ", state.modalOnboarding);
        if (tmp.practiceId) {
          state.modalPractice = state.practices[tmp.practiceId];
        } else {
          console.log("No practice found for: ", onboardingId);
          state.modalPractice = null;
        }
        state.modalStage = stage;
        state.showModal = true;
      }
      else {
        console.log("Error loading: ", onboardingId, stage);
        console.log("Onboardings: ", state.onboardings);
      }
    },
    clearModalOnboarding: (state) => {
      //state.modalOnboarding = null;
      //state.modalPractice = null;
      state.showModal = false;
      //state.modalStage = 'overview';
    },
    setModalStage: (state, action) => {
      console.log("Setting modal stage: ", action.payload);
      state.modalStage = action.payload;
    },
    setShowModal: (state) => {
      state.showModal = !state.showModal;
    },
    setModalCreateOnboarding: (state) => {
      state.showCreateModal = !state.showCreateModal;
    },
    clearError: (state) => {
      state.error = null;
    },
    toggleFilterDsoOnboardings: (state) => {
      state.filterDsoOnboardings = !state.filterDsoOnboardings;
    },
    toggleSortDsoOnboardings: (state) => {
      state.sortDsoOnboardings = !state.sortDsoOnboardings;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOnboardings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOnboardings.fulfilled, (state, action) => {
        console.log("New onboardings: ", action.payload);
        state.onboardings = action.payload;
        state.loading = false;
      })
      .addCase(fetchOnboardings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Heartbeat updates
      .addCase(heartbeat.pending, (state) => {
        // Don't set loading for heartbeat to avoid UI flicker
      })
      .addCase(heartbeat.fulfilled, (state, action) => {
        const newHeartbeats = action.payload.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        state.lastHeartbeat = new Date().toISOString();
        console.log("New heartbeats: ", newHeartbeats);
        // Compare timestamps and track changes
        const changedIds = [];
        const newRowIds = {};
        const newTimestamps = {};
        let i = 0;
        
        newHeartbeats.forEach(newHeartbeat => {
          const oldTimestamp = state.timestamps[newHeartbeat.id];
          const newTimestamp = newHeartbeat._ts;
          
          if (oldTimestamp === newTimestamp) {
            return;
          }

          changedIds.push(newHeartbeat.id);
          newTimestamps[newHeartbeat.id] = newTimestamp;
          newRowIds[newHeartbeat.id] = i;
          i++;          
        });
        
        // Update state properly
        state.heartbeats = newHeartbeats;
        state.changedOnboardingIds = changedIds;
        state.rowId = newRowIds;
        state.timestamps = { ...state.timestamps, ...newTimestamps };

        if (changedIds.length > 0) {
          console.log('Heartbeat: Updated onboardings:', changedIds, 'at', new Date().toLocaleTimeString());
        } else {
          console.log('Heartbeat: No changes detected at', new Date().toLocaleTimeString());
        }
      })
      .addCase(heartbeat.rejected, (state, action) => {
        console.error('Heartbeat failed:', action.error.message);
      })
      .addCase(fetchOnboarding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOnboarding.fulfilled, (state, action) => {
        const newOnboarding = action.payload;

        console.log("New onboarding: ", newOnboarding);
        state.loading = false;

        // Find existing onboarding and update it
        const existingIndex = state.onboardings.findIndex(onboarding => onboarding.id === newOnboarding.id);
        if (existingIndex !== -1) {
          const oldOnboarding = state.onboardings[existingIndex];
          const changedCells = [];

          // Compare stage history lengths
          Object.keys(newOnboarding.stages || {}).forEach((stageName) => {
            const oldLength = oldOnboarding.stages?.[stageName]?.history?.length || 0;
            const newLength = newOnboarding.stages?.[stageName]?.history?.length || 0;
            if (oldLength !== newLength) {
              changedCells.push(stageName);
            }
          });

          // Update the onboarding
          state.onboardings[existingIndex] = newOnboarding;
          
          // Store changed cells for this onboarding
          if (changedCells.length > 0) {
            state.history_lengths[newOnboarding.id] = changedCells;
          }
        } else {
          // Add new onboarding
          console.log("Adding new onboarding: ", newOnboarding);
          state.onboardings.push(newOnboarding);
        }
      })
      .addCase(fetchOnboarding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPractice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPractice.fulfilled, (state, action) => {
        state.practices = action.payload;
        state.loading = false;
        
        state.practices[action.payload.id] = action.payload.practice;

      })
      .addCase(fetchPractice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createOnboarding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOnboarding.fulfilled, (state, action) => {
        state.onboardings.push(action.payload);
        state.loading = false;
        state.showCreateModal = false;
      })
      .addCase(createOnboarding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchEmptyOnboarding.fulfilled, (state, action) => {
        state.emptyOnboarding = action.payload;
      })
      .addCase(fetchEmptyOnboarding.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(markCheckListItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markCheckListItem.fulfilled, (state, action) => {
        state.loading = false;
        // Update modalOnboarding with the returned onboarding object
        state.modalOnboarding = action.payload;
       
        // Also update the onboarding in the onboardings array
        const existingIndex = state.onboardings.findIndex(onboarding => onboarding.id === action.payload.id);
        if (existingIndex !== -1) {
          state.onboardings[existingIndex] = action.payload;
        }
      })
              .addCase(markCheckListItem.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        })
        .addCase(updateOnboardingFeature.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(updateOnboardingFeature.fulfilled, (state, action) => {
          state.loading = false;
          // Update modalOnboarding with the returned onboarding object
          state.modalOnboarding = action.payload;
        
          // Also update the onboarding in the onboardings array
          const existingIndex = state.onboardings.findIndex(onboarding => onboarding.id === action.payload.id);
          if (existingIndex !== -1) {
            state.onboardings[existingIndex] = action.payload;
          }
        })
        .addCase(updateOnboardingFeature.rejected, (state, action) => {
          // state.loading = false;
          // state.error = action.error.message;
        })
        .addCase(linkToPractice.pending, (state) => {
          // state.loading = true;
          // state.error = null;
        })
        .addCase(linkToPractice.fulfilled, (state, action) => {
          // state.loading = false;
          // Update modalOnboarding with the returned onboarding object
          state.modalOnboarding = action.payload;
        
          // Also update the onboarding in the onboardings array
          const existingIndex = state.onboardings.findIndex(onboarding => onboarding.id === action.payload.id);
          if (existingIndex !== -1) {
            state.onboardings[existingIndex] = action.payload;
          }
        })
        .addCase(linkToPractice.rejected, (state, action) => {
          // state.loading = false;
          // state.error = action.error.message;
        })
        .addCase(updateProductType.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(updateProductType.fulfilled, (state, action) => {
          state.loading = false;
          // Update modalOnboarding with the returned onboarding object
          state.modalOnboarding = action.payload;
        
          // Also update the onboarding in the onboardings array
          const existingIndex = state.onboardings.findIndex(onboarding => onboarding.id === action.payload.id);
          if (existingIndex !== -1) {
            state.onboardings[existingIndex] = action.payload;
          }
        })
        .addCase(updateProductType.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        });
       
  }
});

export const { 
  clearChangedOnboardingIds,
  updateTimestamp, 
  clearChangedCells,
  setModalOnboarding,
  clearModalOnboarding,
  setModalStage,
  setShowModal,
  setModalCreateOnboarding,
  clearError,
  toggleFilterDsoOnboardings,
  toggleSortDsoOnboardings
} = onboardingSlice.actions;

// Selectors
// export const selectBaseUrl = (state) => state.onboarding.baseUrl;

export const selectAllOnboardings = (state) => state.onboarding.onboardings;

// export const selectAllOnboardings = (state) => state.onboarding.onboardings;
export const selectAllOnboardingIds = (state) => state.onboarding.onboardings.map(onboarding => onboarding.id);
export const selectAllHeartbeats = (state) => state.onboarding.heartbeats;
export const selectOnboardingById = (state, id) => state.onboarding.onboardings.find(onboarding => onboarding.id === id);
export const selectOnboardingLoading = (state) => state.onboarding.loading;
export const selectOnboardingError = (state) => state.onboarding.error;
// export const selectLastHeartbeat = (state) => state.onboarding.lastHeartbeat;
export const selectChangedOnboardingIds = (state) => state.onboarding.changedOnboardingIds;
export const selectOnboardingTimestamp = (state, id) => state.onboarding.timestamps[id];
export const selectHasOnboardingChanged = (state, id) => state.onboarding.changedOnboardingIds.includes(id);

export const selectHasCellChanged = (state, id, cellType) => {
  const changedCells = state.onboarding.history_lengths[id] || [];
  return changedCells.includes(cellType);
};

export const selectOnboardingStage = (state, id, stage) => {
  const onboarding = state.onboarding.onboardings.find(onboarding => onboarding.id === id);
  if (!onboarding || !onboarding.stages) {
    return null;
  }
  return onboarding.stages[stage] || null;
}

export const selectPracticeById = (state, id) => state.onboarding.practices[id];

// Modal selectors
export const selectModalOnboarding = (state) => state.onboarding.modalOnboarding;
export const selectModalPractice = (state) => state.onboarding.modalPractice;
export const selectModalStage = (state) => {
  console.log("Modal stage: ", state.onboarding.modalStage);
  return state.onboarding.modalStage;
}
export const selectShowModal = (state) => state.onboarding.showModal;
export const selectFilterDsoOnboardings = (state) => state.onboarding.filterDsoOnboardings;
export const selectSortDsoOnboardings = (state) => state.onboarding.sortDsoOnboardings;
// Modal For Creating New Onboarding
export const selectShowCreateModal = (state) => state.onboarding.showCreateModal;
export const selectEmptyOnboarding = (state) => state.onboarding.emptyOnboarding;

export default onboardingSlice.reducer;
